import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFormFields } from '../services/api';
import api from '../services/api';

const EditPackage = () => {
  const { refId } = useParams();               // from /dashboard/edit-package/:refId
  const { state } = useLocation();             // package passed via navigate state
  const navigate = useNavigate();

  const pkg = state?.package || {};
  const [formData, setFormData] = useState(
    typeof pkg.data === 'string' ? JSON.parse(pkg.data || '{}') : pkg.data || {}
  );
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // reference_id: prefer URL param, fall back to state
  const referenceId = refId || pkg.reference_id;

  useEffect(() => {
    if (!referenceId) {
      alert('No package selected for editing.');
      navigate('/dashboard/view-packages');
      return;
    }
    getFormFields()
      .then(res => {
        const fields = Array.isArray(res.data) ? res.data : [];
        setFormFields(fields);
      })
      .catch(err => {
        console.error('Error fetching form fields:', err);
        setError('Failed to load form fields.');
      })
      .finally(() => setLoading(false));
  }, [referenceId, navigate]);

  const handleChange = (e, fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put(`/packages/${referenceId}/edit`, {
        data: formData,
        currency: pkg.currency || 'USD',
        convertedCurrency: pkg.converted_currency || null,
      });
      alert('Package updated successfully!');
      navigate('/dashboard/view-packages');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setError('Error updating package: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <span className="ml-4 text-gray-600">Loading form...</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Package</h2>
          <p className="text-gray-500 mt-1">
            Reference ID: <span className="font-mono font-semibold text-indigo-600">{referenceId}</span>
          </p>
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Only pending packages can be edited
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-xl shadow-md p-6">
          <form onSubmit={handleSubmit}>
            {formFields.length > 0 ? (() => {
              const sections = {};
              formFields.forEach(field => {
                const sec = field.section || 'General';
                if (!sections[sec]) sections[sec] = [];
                sections[sec].push(field);
              });
              return Object.entries(sections).map(([section, fields]) => (
                <div key={section} className="mb-8">
                  <h3 className="text-lg font-semibold text-indigo-700 border-b border-indigo-100 pb-2 mb-4">{section}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {fields.sort((a, b) => a.order_num - b.order_num).map(field => (
                      <div key={field.id} className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.field_name}
                          {field.is_required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type={field.field_type === 'number' ? 'number' : 'text'}
                          value={formData[field.field_name] || ''}
                          onChange={e => handleChange(e, field.field_name)}
                          required={!!field.is_required}
                          className="p-2.5 border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })() : (
              <p className="text-gray-500 text-center py-8">No form fields found.</p>
            )}

            <div className="mt-6 pt-4 border-t flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={saving}
                className={`bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                disabled={saving}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium transition-colors"
                onClick={() => navigate('/dashboard/view-packages')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPackage;